// Persists "what I last selected" across page reloads — the sidebar's
// checked accounts and which account card was expanded. Stores minimal
// identifying refs (name + exchange), not full IPortfolioPerformanceAccounts
// objects, so a stale symbols/last_tick field on a saved object can never
// diverge from what the backend actually returns on the next load; callers
// reconcile refs against the currently-available accounts pool.
//
// Every localStorage call is wrapped — private browsing, storage disabled,
// or quota errors should never break the page, just mean selection isn't
// remembered for that session.

export interface AccountRef {
  name: string;
  exchange: string;
}

const SELECTED_ACCOUNTS_KEY = 'pp_selected_accounts';
const OPEN_ACCOUNT_KEY = 'pp_open_account_name';

export const saveSelectedAccountRefs = (accounts: AccountRef[]): void => {
  try {
    localStorage.setItem(
      SELECTED_ACCOUNTS_KEY,
      JSON.stringify(accounts.map(({ name, exchange }) => ({ name, exchange }))),
    );
  } catch {
    /* ignore */
  }
};

// Returns null when nothing has ever been saved (first-ever visit — caller
// should apply its own defaults), distinct from an empty array (the user
// previously deselected everything, which should be respected as-is).
export const loadSelectedAccountRefs = (): AccountRef[] | null => {
  try {
    const raw = localStorage.getItem(SELECTED_ACCOUNTS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (item): item is AccountRef =>
        !!item && typeof item.name === 'string' && typeof item.exchange === 'string',
    );
  } catch {
    return null;
  }
};

export const saveOpenAccountName = (name: string | null): void => {
  try {
    if (name === null) localStorage.removeItem(OPEN_ACCOUNT_KEY);
    else localStorage.setItem(OPEN_ACCOUNT_KEY, name);
  } catch {
    /* ignore */
  }
};

export const loadOpenAccountName = (): string | null => {
  try {
    return localStorage.getItem(OPEN_ACCOUNT_KEY);
  } catch {
    return null;
  }
};
